import React, { useEffect, useState } from 'react';

import { ssoAPI } from '../../common/api';

import AuthFormContainer from '../../common/components/auth-form-container';
import BannerMessage from '../../common/components/banner-message';
import DangerString from '../../common/components/danger-string';
import ThirdPartyForm from '../../common/components/third-party-form';
import IString from '../../common/components/i-string';
import InviteMessage from '../../common/components/invite-message';
import SSOLoginForm from '../../common/components/sso-login-form';

import { translate } from '../../common/i18n/localize';

import * as authMethodUtils from '../../common/util/auth-method-util';
import * as serverUtils from '../../common/util/server-util';

import * as errors from '../constants/errors';

import ChangeLoginForm from './change-login-form';
import EmailForm from './email-form';
import LoginFormContainer from './login-form-container';
import PasswordForm from './password-form';

const EMAIL_SENT = 'unpair_email_sent_screen';
const START = 'start_screen';
const PASSWORD = 'password_screen';
const SSO_USER = 'sso_user_screen';
const SSO_DEDICATED = 'dedicated_sso_screen';
const NOT_AUTHORIZED = 'sso_not_authorized';

function Login() {
  const [email, setEmail] = useState(serverUtils.getEmail());
  const [error, setError] = useState(serverUtils.getError());
  const inviteCode = serverUtils.getInviteCode();
  const redirect = serverUtils.getRedirect();
  const [ssoInfo, setSSOInfo] = useState(serverUtils.getSSO());
  const product = serverUtils.getProduct();

  const [screen, setScreen] = useState(ssoInfo ? SSO_DEDICATED : START);

  useEffect(() => {
    if (error?.error_type === NOT_AUTHORIZED) {
      setScreen(SSO_USER);
    }
  }, [error]);

  const detectPath = (email) => {
    setEmail(email);

    let screen = PASSWORD;

    ssoAPI.isSSO(email, product).then(result => {
      if (result) {
        // this user is paired to an SSO account and must log in that way
        setSSOInfo(result);
        screen = SSO_USER;
      }
    }).catch(() => {
      // any errors here mean we have no SSO option to show
      // so carry on
    }).finally(() => {
      // we're switching screens, clear out any prior errors
      setError();
      setScreen(screen);
    });
  };
  
  const handleUnpairEmailSubmit = (email) => {
    setError();
    ssoAPI.sendUnpairingEmail(email, ssoInfo.idp, inviteCode, redirect, product).then(() => {
      setEmail(email);
      setScreen(EMAIL_SENT);
    }).catch((error) => {
      setError(error.data);
    });
  };

  const authMethods = serverUtils.getAuthMethods();
  const showAll = authMethodUtils.allowAll(authMethods);
  const showLocal = authMethods[authMethodUtils.LOCAL_AUTH_METHOD];
  const showThirdParty = authMethodUtils.hasThirdPartyAuthMethod(authMethods);

  return (
    <>
      { error && 
        <BannerMessage className='onPage marginBelow' type='error' onClose={() => setError()}>
          { errors.isKnownError(error.error_type) 
            ? <IString stringKey={`errors.${error.error_type}`} placeholders={errors.formatErrorPlaceholders(error)} />
            : error.error }
        </BannerMessage> }

      <InviteMessage />

      { screen === START && 
        <LoginFormContainer>
          {(showAll || showLocal) &&
            <>
              <EmailForm email={email} onSubmit={detectPath} />

              {(showAll || showThirdParty) &&
                <div className='center lightText midMarginAbove midMarginBelow'>
                  <IString stringKey='general.or' />
                </div>
              }
            </>
          }

          {(showAll || showThirdParty) &&
            <div className='formish backgroundless'>
              {authMethodUtils.THIRD_PARTY_AUTH_METHODS.map((authMethod, i) => {
                return (showAll || authMethods[authMethod]) &&
                  <ThirdPartyForm
                    className={i === 0 ? 'noMarginAbove' : 'marginAbove'}
                    key={authMethod}
                    thirdPartyName={authMethod}
                  />;
              })}
            </div>
          }
        </LoginFormContainer> }

      { screen === PASSWORD && 
        <LoginFormContainer>
          <button type='button' className='backButton' aria-label={translate('general.back')} onClick={() => setScreen(START)} />
          <PasswordForm email={email} />
        </LoginFormContainer> }

      { screen === EMAIL_SENT &&
        <AuthFormContainer title={<IString stringKey='sso.unpair.sent.title' />}>
          <div className='formish'>
            <DangerString stringKey='sso.unpair.sent.message' placeholders={{ email }} />
          </div>
        </AuthFormContainer> }

      { screen === SSO_USER &&
        <AuthFormContainer title={<IString stringKey='general.title.login' />}>
          {/* We cannot depend on the error state because it clears once the error is closed
            The error object exists in the server still so fetch it to give the right state */}
          { serverUtils.getError()?.error_type === NOT_AUTHORIZED ?
            <div className='messageContainer mark urgent marginAbove'>
              <div className='message'>
                <p><DangerString stringKey='sso.not_authorized' placeholders={{ name: ssoInfo?.name }} trustPlaceholders={true} /></p>
              </div>
            </div>
            :  
            <>
              <button type='button' className='backButton' aria-label={translate('general.back')} onClick={() => setScreen(START)} />
              <SSOLoginForm {...ssoInfo} context='user' /> 
            </> }

          <ChangeLoginForm email={email} onSubmit={handleUnpairEmailSubmit} ssoInfo={ssoInfo} />
        </AuthFormContainer> }

      { screen === SSO_DEDICATED && 
        <AuthFormContainer title={<IString stringKey='general.title.login' />}>
          <SSOLoginForm {...ssoInfo} context='dedicated' />

          <ChangeLoginForm email={email} onSubmit={handleUnpairEmailSubmit} ssoInfo={ssoInfo} />
        </AuthFormContainer> }
    </>
  );
}

export default Login;
